import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { getAuth, sendPasswordResetEmail } from 'firebase/auth'
import { doc, getFirestore, setDoc } from 'firebase/firestore'
import { app } from '../../firebase'
import { useLawAuth } from '../auth/LawAuthProvider'
import { translations, type Language } from '../../translations'

interface ProfilePageProps {
    lang: Language
}

export function ProfilePage({ lang }: ProfilePageProps) {
    const t = translations[lang]
    const navigate = useNavigate()
    const { user, profileData, setProfileData } = useLawAuth()

    // Local state to prevent overwriting by background snapshots while typing
    const [localProfileData, setLocalProfileData] = useState(profileData)
    const [resetSent, setResetSent] = useState(false)
    const [resetError, setResetError] = useState('')
    const [isSaving, setIsSaving] = useState(false)

    // Sync local state from global profile data only if local is empty/pristine 
    // and global has data (e.g. initial load completed)
    useEffect(() => {
        if (profileData.fullName || profileData.profession || profileData.phone || profileData.city) {
            setLocalProfileData(prev => {
                // Determine if local state is effectively "empty" (user hasn't typed meaningful data yet)
                const isLocalEmpty = !prev.fullName && !prev.profession && !prev.phone && !prev.city

                // If local is empty, safe to sync from DB
                if (isLocalEmpty) {
                    return profileData
                }

                // If not empty, we generally don't want to overwrite user input.
                // However, on first mount `profileData` might be empty then populate.
                // The `isLocalEmpty` check handles the "populated later" case.
                return prev
            })
        }
    }, [profileData])

    // Redirect if not logged in
    useEffect(() => {
        if (!user) {
            navigate('/')
        }
    }, [user, navigate])

    const handlePasswordReset = async () => {
        if (!user?.email) return
        try {
            const auth = getAuth()
            await sendPasswordResetEmail(auth, user.email)
            setResetSent(true)
            setResetError('')
        } catch (error) {
            console.error('Password reset failed', error)
            setResetError('Error sending reset email')
        }
    }

    const handleLocalSave = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!user) return

        setIsSaving(true)
        try {
            const db = getFirestore(app, 'dxt-law-db')
            const userRef = doc(db, 'users', user.uid)

            // Optimistic update of global state so UI reflects changes immediately
            setProfileData(localProfileData)

            await setDoc(userRef, {
                email: user.email,
                fullName: localProfileData.fullName,
                companyName: localProfileData.companyName,
                profession: localProfileData.profession,
                phone: localProfileData.phone,
                city: localProfileData.city,
                updated_at: new Date().toISOString()
            }, { merge: true })

            // Optional: Show success message or toast
        } catch (error) {
            console.error('Error saving profile', error)
            alert(t.errorGeneric)
        } finally {
            setIsSaving(false)
        }
    }

    if (!user) return null

    return (
        <div className="min-h-screen bg-slate-50 py-8 px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-2xl">
                <div className="mb-8 flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-slate-900">{t.titleProfilePage}</h1>
                        <p className="mt-1 text-sm text-slate-500">{t.profileSubtitle}</p>
                    </div>
                    <button
                        onClick={() => navigate('/')}
                        className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                    >
                        &larr; Back
                    </button>
                </div>

                <div className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm sm:p-8">
                    <form onSubmit={handleLocalSave} className="space-y-6">

                        {/* Full Name */}
                        <div>
                            <label className="mb-1 block text-sm font-medium text-slate-700">
                                {t.labelFullName}
                            </label>
                            <input
                                type="text"
                                required
                                value={localProfileData.fullName}
                                onChange={(e) => setLocalProfileData({ ...localProfileData, fullName: e.target.value })}
                                className="w-full rounded-xl border border-slate-300 px-4 py-2.5 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                            />
                        </div>

                        {/* Company Name */}
                        <div>
                            <label className="mb-1 block text-sm font-medium text-slate-700">
                                {t.labelCompanyName} <span className="text-slate-400 font-normal">(Optional)</span>
                            </label>
                            <input
                                type="text"
                                value={localProfileData.companyName}
                                onChange={(e) => setLocalProfileData({ ...localProfileData, companyName: e.target.value })}
                                className="w-full rounded-xl border border-slate-300 px-4 py-2.5 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                            />
                        </div>

                        {/* Profession */}
                        <div>
                            <label className="mb-1 block text-sm font-medium text-slate-700">
                                {t.labelProfession}
                            </label>
                            <select
                                required
                                value={localProfileData.profession}
                                onChange={(e) => setLocalProfileData({ ...localProfileData, profession: e.target.value })}
                                className="w-full rounded-xl border border-slate-300 bg-white px-4 py-2.5 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                            >
                                <option value="">{t.placeholderProfession}</option>
                                <option value="lawyer">{t.prof_lawyer}</option>
                                <option value="corporate">{t.prof_corporate}</option>
                                <option value="student">{t.prof_student}</option>
                                <option value="individual">{t.prof_individual}</option>
                                <option value="other">{t.prof_other}</option>
                            </select>
                        </div>

                        <div className="grid gap-6 sm:grid-cols-2">
                            {/* Phone */}
                            <div>
                                <label className="mb-1 block text-sm font-medium text-slate-700">{t.labelPhone}</label>
                                <input
                                    type="tel"
                                    required
                                    placeholder={t.placeholderPhone}
                                    value={localProfileData.phone}
                                    onChange={(e) => setLocalProfileData({ ...localProfileData, phone: e.target.value })}
                                    className="w-full rounded-xl border border-slate-300 px-4 py-2.5 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                                />
                            </div>

                            {/* City */}
                            <div>
                                <label className="mb-1 block text-sm font-medium text-slate-700">{t.labelCity}</label>
                                <input
                                    type="text"
                                    required
                                    placeholder={t.placeholderCity}
                                    value={localProfileData.city}
                                    onChange={(e) => setLocalProfileData({ ...localProfileData, city: e.target.value })}
                                    className="w-full rounded-xl border border-slate-300 px-4 py-2.5 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                                />
                            </div>
                        </div>

                        <div className="pt-4 border-t border-slate-100">
                            <button
                                type="submit"
                                disabled={isSaving}
                                className="w-full rounded-xl bg-blue-600 py-3 px-4 font-bold text-white transition-colors hover:bg-blue-700 disabled:opacity-50 shadow-sm shadow-blue-200"
                            >
                                {isSaving ? (lang === 'fr' ? 'Enregistrement...' : 'جاري الحفظ...') : t.submitProfile}
                            </button>
                        </div>
                    </form>

                    {/* Password Reset Section */}
                    {user.providerData[0]?.providerId === 'password' && (
                        <div className="mt-10 pt-6 border-t border-slate-200">
                            <h3 className="text-base font-semibold text-slate-900">{t.password}</h3>
                            <p className="mt-1 text-sm text-slate-500 mb-4">
                                {lang === 'fr'
                                    ? "Vous pouvez réinitialiser votre mot de passe en cliquant ci-dessous."
                                    : "يمكنك إعادة تعيين كلمة المرور الخاصة بك بالنقر أدناه."}
                            </p>

                            {!resetSent ? (
                                <button
                                    type="button"
                                    onClick={handlePasswordReset}
                                    className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
                                >
                                    {t.btnResetPassword}
                                </button>
                            ) : (
                                <div className="rounded-lg bg-green-50 p-3 text-sm text-green-700 border border-green-200">
                                    {t.msgPasswordResetSent.replace('{email}', user.email || '')}
                                </div>
                            )}
                            {resetError && <p className="mt-2 text-sm text-red-600">{resetError}</p>}
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
